import { Text, Skeleton, Box, Stack } from '@mantine/core';
import type { UseFormReturnType } from '@mantine/form';
import {
  ProductCategoryConfig,
  ProductCategoryOrder,
  type ProductCategoryKey,
  type FormularCFormData,
} from '../../types/reports';
import { FormCard } from '../ui/FormCard';
import { FormSubsection } from '../ui/FormSubsection';
import { SummaryBar } from '../ui/SummaryBar';
import { ValidatedNumberInput } from '../form/validatedFields';
import { PreviousValueForField } from '../form/PreviousValueLine';
import { RequiredMark } from '../form/requiredMark';
import { DECIMAL_INPUT_PROPS, FIELD_LIST_MAX_WIDTH } from '../form/numberFieldPresets';

/** Shape returned by POST /api/reports/calculate for type "formular_c" (FormularCCalculator, 18-01). */
export interface FormularCCalculations {
  inlandsVersand: string | null;
  /** Vergleichswert Inlandsversand vom Server (nur mit Meldungskontext, sonst fehlt er). */
  inlandsversandComparison?: string | null;
  /** Differenz Summe minus Vergleichswert, vom Server berechnet. */
  inlandsversandDifference?: string | null;
}

interface FormularCFormProps {
  form: UseFormReturnType<FormularCFormData>;
  runValidation: () => void;
  /** Server-Vorschau der Summe (D-08) -- strikt read-only, wird nie in die form zurueckgeschrieben. */
  calc: FormularCCalculations | null;
  disabled?: boolean;
  loading?: boolean;
}

/**
 * Praesentations-Shell (D-01): kein zweiter, unabhaengiger Formular-State mehr und kein lokaler
 * Kreuzvalidierungs-Callback -- die Regel "Summe der davon-Felder muss dem Gesamtwert
 * entsprechen" lebt seit 18-02 als Assert\Expression auf der FormularC-Entity und laeuft
 * ueber runValidation -> /api/reports/validate -> form.setErrors (D-14), also ueber denselben
 * Weg wie jede andere Feldpruefung. Die Summenanzeige kommt server-berechnet aus useReportCalc,
 * exakt das waterConsumption-Muster aus WasserSection.
 *
 * Kontrollzeile (Phase 44): Der Vergleichswert ist die Jahressumme der Meldungen Inlandsversand
 * des Werks im Meldejahr, gleich welcher Status; Wert und Differenz kommen vom Server, der
 * Browser rechnet nichts nach.
 */
export function FormularCForm({
  form,
  runValidation,
  calc,
  disabled,
  loading,
}: FormularCFormProps) {
  const renderCategoryField = (categoryKey: ProductCategoryKey) => {
    const config = ProductCategoryConfig[categoryKey];
    const labelPrefix = config.isChild ? 'davon: ' : '';
    // Alle Abnehmergruppen sind Pflicht, die „davon"-Aufteilungen nicht: nicht jedes
    // Werk kann so fein aufschluesseln. Dieselbe Grenze zieht die submit-Gruppe der
    // FormularC-Entity (Assert\NotNull) -- Markierung und Durchsetzung bleiben synchron.
    const isRequired = !config.isChild;

    return (
      <Box key={categoryKey} pl={config.isChild ? 'xl' : undefined}>
        {loading ? (
          <Skeleton height={60} />
        ) : (
          <>
            <ValidatedNumberInput
              form={form}
              field={categoryKey}
              fieldLabel={`${labelPrefix}${config.label}`}
              kind="decimal"
              align="right"
              maw={FIELD_LIST_MAX_WIDTH}
              label={
                <Text component="span">
                  {labelPrefix}
                  {config.label}
                  {/*
                    Das Sternchen steht direkt hinter dem Feldnamen, NICHT am Ende des
                    Labels (Mantines `withAsterisk` wuerde es hinter die Klammer setzen).
                    Es markiert das Feld, nicht dessen Erlaeuterung -- und haengt so nicht
                    an der Laenge des Klammerzusatzes, der pro Zeile stark schwankt und
                    umbrechen kann. Geschuetztes Leerzeichen, damit es nie allein umbricht.
                  */}
                  {isRequired && (
                    <>
                      {' '}
                      <RequiredMark />
                    </>
                  )}
                  {config.description && (
                    <Text span size="xs" c="vdzGray.6">
                      {' '}
                      ({config.description})
                    </Text>
                  )}
                </Text>
              }
              disabled={disabled}
              suffix=" t"
              {...DECIMAL_INPUT_PROPS}
              styles={
                config.isChild
                  ? {
                      root: {
                        borderLeft: '2px solid var(--mantine-color-gray-3)',
                        paddingLeft: 'var(--mantine-spacing-md)',
                      },
                    }
                  : undefined
              }
            />
          </>
        )}
      </Box>
    );
  };

  return (
    <div onBlur={runValidation}>
      <Stack gap="md">
        <FormCard>
          <Stack gap="md">
            <FormSubsection
              title="Versand nach Produktkategorien"
              description="Geben Sie die Versandmengen für jede Produktkategorie in Tonnen ein."
            >
              <Stack gap="sm">{ProductCategoryOrder.map(renderCategoryField)}</Stack>
            </FormSubsection>

            {/* Der Summenbalken steht im Sketch INNERHALB des weissen Rahmens, nicht darunter. */}
            <SummaryBar
              label="Summe Versand nach Produktkategorien"
              value={calc?.inlandsVersand ?? null}
              variant="highlight"
              loading={loading}
              reconciliation={{
                label: 'Inlandsversand',
                value: calc?.inlandsversandComparison ?? null,
                difference: calc?.inlandsversandDifference ?? null,
              }}
            />
            <PreviousValueForField field="inlandsVersand" />
          </Stack>
        </FormCard>
      </Stack>
    </div>
  );
}
